import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLogin, useLogout } from '../hooks/useAuth';
import { useCurrentCompany } from '../hooks/useCompany';
import { getCurrentUser, getAuthToken, isTokenValid, logout as logoutUtil } from '../utils/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  
  // Fetch company data when user is available
  const { data: companyData, isLoading: companyLoading } = useCurrentCompany();

  // Check for existing user on mount and validate token
  useEffect(() => {
    const checkAuthStatus = () => {
      setLoading(true);
      
      try {
        // Check if we have valid token and user data
        if (isTokenValid()) {
          const currentUser = getCurrentUser();
          setUser(currentUser);
        } else {
          // No valid token/user - ensure we're logged out
          setUser(null);
          // Clear any stale data
          localStorage.removeItem('current_user');
          localStorage.removeItem('auth_token');
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setUser(null);
        // Clear potentially corrupted data
        localStorage.removeItem('current_user');
        localStorage.removeItem('auth_token');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await loginMutation.mutateAsync({ email, password });
      
      if (result.success) {
        setUser(result.user);
        return { success: true };
      }
      
      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Login failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const bypassLogin = () => {
    // Set mock user for development/testing with company data
    const mockUser = {
      id: 'dev-user-bypass',
      fullName: 'Development User',
      email: 'dev@accountingpro.com',
      role: 'admin',
      companyId: 'mock-company-1',
      currency: 'USD',
      company: {
        id: 'mock-company-1',
        name: 'AccountingPro Demo',
        email: 'contact@accountingpro.com',
        phone: '(555) 123-4567',
        currency: 'USD',
        address: {
          street: '123 Business Avenue, Suite 100',
          city: 'New York',
          state: 'NY',
          zipCode: '10001',
          country: 'United States',
          email: 'contact@accountingpro.com',
          phone: '(555) 123-4567'
        },
        taxId: 'TAX123456789',
        status: 'active'
      },
      avatarUrl: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setUser(mockUser);
    localStorage.setItem('current_user', JSON.stringify(mockUser));
    localStorage.setItem('auth_token', 'mock-token-' + Date.now());
    return { success: true };
  };

  const logout = async () => {
    try {
      setLoading(true);
      await logoutMutation.mutateAsync();
      
      // Use the utility function to clear all auth data
      logoutUtil();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      logoutUtil();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    companyData,
    loading: loading || loginMutation.isPending || logoutMutation.isPending || companyLoading,
    login,
    logout,
    bypassLogin,
    isAuthenticated: !!user && !!getAuthToken(),
    loginError: loginMutation.error?.message,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;